import React from 'react';
import { createRoot } from 'react-dom/client';
// eslint-disable-next-line
import {
  GoogleOAuthProvider,
  GoogleLogin,
  googleLogout,
} from '@react-oauth/google';
import { SWRConfig } from 'swr';
import { StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material/styles';
import {
  StylesProvider as V4StylesProvider,
  createGenerateClassName as v4CreateGenerateClassName,
} from '@material-ui/core/styles';
import {
  StylesProvider as V5StylesProvider,
  createGenerateClassName as v5CreateGenerateClassName,
} from '@mui/styles';
import { dataFetcher } from 'Api';
import AOS from 'aos';
// import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from 'store/store';
import { resumeUiSwitchOverlay } from 'Utils/uiSwitchOverlay';
// import { isNewUIEnabled } from 'hooks/useNewUI';
import { bootUiConfig, isNewUIEnabled } from 'hooks/useNewUI';
import { initQaBridge } from 'qa/qaBridge';
import GoogleJson from './Config/Google.json';
import { App } from './app';
// import './index.css';
import 'aos/dist/aos.css';

// If a new⇄classic UI switch is mid-reload, cover the screen before React paints
// so the fresh app mounts behind the overlay (no page flash).
resumeUiSwitchOverlay();

// QA automation bridge — only activates with ?qa_bridge=1 (admin Testing page).
initQaBridge();

AOS.init();
// MUI v5 default theme, shared by emotion (@mui/material) and the @mui/styles JSS
// bridge (makeStyles/withStyles). injectFirst puts emotion's styles at the top of
// <head> so the app's CSS-modules/JSS keep winning the specificity war (as they did
// under MUI v4) instead of being overridden by emotion.
const muiTheme = createTheme();
// This branch runs TWO JSS engines side-by-side: v4 (@material-ui/core, 18 files)
// and v5 (@mui/styles, 59 files). By default both emit global `jss1/jss2...` class
// names from independent counters, so in a production build (readable names dropped)
// a v4 and a v5 component can be assigned the SAME `jssN` and steal each other's
// styles — the real cause of the /access layout break that never reproduced locally.
// Seeding each engine differently makes every class name unique across the two.
const v4GenerateClassName = v4CreateGenerateClassName({ seed: 'v4' });
const v5GenerateClassName = v5CreateGenerateClassName({ seed: 'v5' });
const root = createRoot(document.getElementById('root'));
// The backend's getConfig decides new vs classic UI, so settle it before the
// first paint. Boot never fails on it: a slow or failed call falls back to the
// cached config.
// root.render( // old: rendered immediately, before getConfig
bootUiConfig().then(() => root.render(
  <Provider store={store}>
    <SWRConfig
      value={{
        fetcher: dataFetcher,
        revalidateOnFocus: false,
        revalidateOnMount: false,
        shouldRetryOnError: false,
      }}
    >
      {/* injectFirst (emotion styles first, so app CSS wins) is required by the
          NEW UI, but it flips MUI's CSS priority app-wide and misaligns the OLD
          UI's MUI Select/Menu dropdowns (the old-UI branches never had it). Gate
          it on the UI mode so classic users get the exact MUI environment the
          old production had. */}
      <V4StylesProvider generateClassName={v4GenerateClassName}>
        <V5StylesProvider generateClassName={v5GenerateClassName}>
          <StyledEngineProvider injectFirst={isNewUIEnabled()}>
            <ThemeProvider theme={muiTheme}>
              <GoogleOAuthProvider clientId={GoogleJson?.web?.client_id}>
                <App />
              </GoogleOAuthProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </V5StylesProvider>
      </V4StylesProvider>
    </SWRConfig>
  </Provider>,
));
// reportWebVitals();
